// Client-side types + fetcher for the portfolio dashboard endpoint.

import type { GridContext } from '../components/portfolio/GridContextPanel';
import type { PipelineReference } from './pipeline-reference';

export type Tone = 'pass' | 'caution' | 'fail' | 'neutral';

export interface PortfolioDimension {
  grade: string;
  driver: string;
  tone: Tone;
  /** Evidence bullets shown in the expanded per-dimension detail card. */
  evidence?: string[];
}

/** A single IC-style score group (Fuel Delivery / Site Readiness / Acquisition).
    Grades come from parcel_grades; for carve-out rep parcels the server
    synthetically recomputes fuel/site/acquisition against assembly inputs so
    the side panel matches the briefing's grade tiles. */
export interface PortfolioGroupScore {
  grade: string | null;
  score: number | null;
  percentile: number | null;
}

export interface PortfolioGroupScores {
  fuel_delivery: PortfolioGroupScore;
  site_readiness: PortfolioGroupScore;
  acquisition: PortfolioGroupScore;
}

export interface PortfolioSiteRisk {
  value: string;
  label: string;
  tone: Tone;
  note?: string | null;
}

// Per-row knockout-gate evaluation. Each gate is a boolean trip plus a short
// detail string suitable for a chip ("Zone AE · 28%", "Slope 12.4%"). `list`
// gives an ordered set of tripped gate keys (BTM IC scan order: gas → size →
// owner → air → aml → flood → slope); `count` is the cardinality.
export type KnockoutKey = 'gas' | 'size' | 'owner' | 'air' | 'aml' | 'flood' | 'slope';
export interface PortfolioKnockouts {
  gas: boolean;
  size: boolean;
  owner: boolean;
  air: boolean;
  aml: boolean;
  flood: boolean;
  slope: boolean;
  count: number;
  list: KnockoutKey[];
  detail: Record<KnockoutKey, string | null>;
}

// Informational flags — softer than knockouts. EJ = community opposition risk
// (EJScreen percentile ≥ 90); HABITAT = federal critical-habitat overlap.
// Surfaced as data badges, not screens.
export type FlagKey = 'ej' | 'habitat';
export interface PortfolioFlags {
  ej: boolean;
  habitat: boolean;
  count: number;
  list: FlagKey[];
  detail: Record<FlagKey, string | null>;
}

// Friendly corridor identity used for grouping rows. Falls back through
// pipeline_available_name → gas_supply_zone_slug → "Unclassified".
export interface PortfolioCorridor {
  id: string;
  label: string;
  diameter_in: number | null;
  source: 'pipeline' | 'supply_zone' | 'none';
}

export interface PortfolioRow {
  site_evaluation_id: string;
  parcel_id: string;
  rank: number | null;
  priority_tag: 'priority_1' | 'fixable' | 'park' | null;
  priority_label: string | null;
  county: string;
  state: string;
  site_label: string | null;
  pipeline: { name: string; diameter_in: number | null } | null;
  corridor: PortfolioCorridor;
  carve_out: {
    slug: string;
    name: string;
    parcel_count: number;
    listing_acres: number | null;
    polygon_acres: number | null;
  } | null;
  key_numbers: {
    acres: number | null;
    available_mw: number | null;
    /** TCEQ/EPA ozone attainment band. 3-state: Clean (~10-12mo permit) /
        NAA (24+mo or denied) / unknown. Standalone cell so the IC can scan
        permit risk independent of MW headroom. */
    air_permit: PortfolioSiteRisk;
    site_risk: PortfolioSiteRisk;
  };
  /** Three IC-style score groups — same triplet rendered in the briefing's
      DimensionBar block. Appears at the top of the side panel above the 5
      Integra dimensions. */
  group_scores: PortfolioGroupScores;
  /** Integra's 5 priority dimensions, ordered Power → Land → Ownership → Fiber → Water.
      Display-only — overall grade letter still uses BTM-optimized dc_suitability_score. */
  dimensions: {
    power: PortfolioDimension;
    land: PortfolioDimension;
    ownership: PortfolioDimension;
    fiber: PortfolioDimension;
    water: PortfolioDimension;
  };
  knockouts: PortfolioKnockouts;
  flags: PortfolioFlags;
  centroid: { lng: number; lat: number } | null;
  bbox: [number, number, number, number] | null;
  /** Dissolved assembly outline (or the single parcel, for non-carve-out
      sites) as a GeoJSON geometry — the map's default "Assemblies" layer. */
  geometry: GeoJSON.Geometry | null;
  /** Individual member-parcel polygons inside the assembly — the map's
      "Parcels" toggle layer. For single-parcel sites this is the parcel itself. */
  member_geometries: GeoJSON.Geometry[] | null;
  /** Largest contiguous buildable block after netting out FEMA 100-yr flood +
      NWI wetlands (assembly_clean_pads). null when not computed for this site. */
  clean_pad_acres: number | null;
  /** Total buildable across ALL clean pads ≥ the compute's min-piece cutoff
      (crownpine mode). null for single-largest-pad assemblies (Integra). */
  total_clean_acres: number | null;
  /** The clean pad polygon — the map's "Clean pad" layer. */
  clean_pad_geometry: GeoJSON.Geometry | null;
  avg_slope_pct: number | null;
  flood_zone_pct_100yr: number | null;
  resolve_count: number;
  disclosure_statuses: string[];
  score: number | null;
  grade: string | null;
  pipeline_reference?: PipelineReference | null;
  dc_score_version?: string | null;
  dc_score_basis?: 'reference_scenario' | 'retained' | 'unassessed';
  pipeline_delivery_status?: 'unverified';
  display_note: string | null;
  vintage: string[];
}

export interface PortfolioCycle {
  no: number;
  date: string;
  memo_md: string;
  concentration_callout: string | null;
}

export interface PortfolioTotals {
  sites: number;
  acres_total: number;
  /** Served as null: pipeline delivery is unverified, and a per-site sum double-counts shared pipelines. */
  available_mw_total: number | null;
  loi_ready_count: number;
  fixable_count: number;
  drop_count: number;
}

// Auto-derived analytics that sit above the row list. Replaces the
// hand-authored cover memo at portfolio scale. Everything here is computed
// in routes/portfolio.js#buildSynthesis from the rows array — no new SQL.
export interface PortfolioCorridorAggregate {
  id: string;
  label: string;
  source: 'pipeline' | 'supply_zone' | 'none';
  diameter_in: number | null;
  tract_count: number;
  total_mw: number;
  max_mw: number;
  dominant_state: string | null;
}

export interface PortfolioStateRow {
  state: string;
  tract_count: number;
  acres_total: number;
  available_mw_total: number;
  knockout_count: number;
  clean_count: number;
  median_grade: string | null;
}

export interface PortfolioLoiReady {
  count: number;
  parcel_ids: string[];
  median_acres: number | null;
  median_grade: string | null;
  state_breakdown: Record<string, number>;
  criteria: {
    pipeline_available_mw_min: number;
    avg_slope_pct_max: number;
    knockout_count: number;
  };
}

export interface PortfolioDisclosureSummary {
  tracts_with_open: number;
  by_status: {
    pending_specifications: number;
    operator_confirmed: number;
    mixed_or_other: number;
  };
  otherwise_grade_a: number;
}

export interface PortfolioSynthesis {
  grade_distribution: Record<string, number>;
  grade_summary: {
    total: number;
    ungraded: number;
    a_or_better: number;
    b_or_better: number;
  };
  knockout_summary: {
    total: number;
    gas: number;
    size: number;
    owner: number;
    air: number;
    aml: number;
    flood: number;
    slope: number;
    two_plus: number;
    any: number;
    clean: number;
  };
  flag_summary: {
    total: number;
    ej: number;
    habitat: number;
    any: number;
  };
  corridor_concentration: {
    top: PortfolioCorridorAggregate[];
    tail: { tract_count: number; corridor_count: number; total_mw: number };
    total_corridors: number;
    top_n_share_pct: number;
  };
  state_summary: PortfolioStateRow[];
  loi_ready_picks: PortfolioLoiReady;
  disclosure_summary: PortfolioDisclosureSummary;
  bbox: [number, number, number, number] | null;
}

/** One independently-ranked buildable pad — a single contiguous clean piece
    (≥ the compute's min-piece cutoff) exploded out of a block's clean-pad
    MultiPolygon. Crown Pine research mode only; the unit of the /portfolio/crownpine
    list + map. Each pad has its OWN acreage / slope / flood, and a back-reference
    to the ownership block it came from. */
/** Precomputed logistics facts for a Crown Pine pitch site (crownpine_site_logistics).
    Drive distances are real road routes; airport/grid fields are nearest-facility
    lookups. Shown on the Inquiry-sites logistics card. */
export interface PortfolioSiteLogistics {
  /** True when this is A2 borrowing A1's logistics (A2 is dependent on A1). */
  inherited: boolean;
  drive_iah_mi: number | null;
  drive_iah_min: number | null;
  drive_lufkin_mi: number | null;
  drive_lufkin_min: number | null;
  jet_airport_name: string | null;
  jet_airport_mi: number | null;
  jet_runway_ft: number | null;
  commercial_airport_name: string | null;
  commercial_airport_mi: number | null;
  commercial_runway_ft: number | null;
  nearest_airports: Array<{ name: string; mi: number; runway_ft: number | null }> | null;
  substation_name: string | null;
  substation_operator: string | null;
  substation_kv: number | null;
  substation_mi: number | null;
  transmission_kv: number | null;
  transmission_mi: number | null;
}

/** One screened gas corridor for a shortlisted block. `primary` is the selected
    Energy Transfer supply corridor; `additional` is the screened Kinder Morgan
    corridor. All distances are straight-line screens, not routes. */
export interface PortfolioLrmGasOption {
  family: 'ET' | 'KM';
  name: string;
  operator: string | null;
  operator_parent: string | null;
  diameter_in: number | null;
  campus_distance_mi: number | null;
  crosses_block: boolean;
  /** Connector miles running on the supplied Crown Pine inventory. */
  cp_mi: number | null;
  /** Connector miles outside supplied Crown Pine inventory. */
  outside_mi: number | null;
  cp_pct: number | null;
  gap_count: number | null;
  longest_gap_mi: number | null;
  curation_note: string | null;
  /** Split connector geometry — present on the primary option only. */
  cp_segments?: GeoJSON.Geometry | null;
  outside_segments?: GeoJSON.Geometry | null;
  /** Parcels of record the outside-inventory run crosses, per CAD owner with
      crossed miles — primary option only; null where unscreened. */
  outside_owner_screen?: {
    parcel_count: number;
    owners: { name: string; parcels: number | null; mi: number | null }[];
  } | null;
}

export interface PortfolioLrmGas {
  version: number;
  primary: PortfolioLrmGasOption;
  additional: PortfolioLrmGasOption | null;
  /** An alternative tap reaching more of the supplied Crown Pine inventory,
      where the screen found one. */
  alt_on_cp_tap: Record<string, unknown> | null;
}

/** The same buildable pipeline re-run with mapped intermittent streambeds
    (NWI R4) treated as engineered crossings rather than severing features:
    pieces a dry seasonal channel would otherwise cut apart rejoin, and the
    streambed's own acreage is deducted (it is engineered around/over, not
    developable ground). A sibling reading alongside the screened
    `total_ac`/`piece_count`/`pieces` on `PortfolioLrmBuildable` — never a
    replacement for it. */
export interface BuildableEffective {
  /** NET of in-piece streambed acreage: sum of effective piece areas minus
      `streambed_ac`. Always ≥ the screened `total_ac`. */
  total_ac: number;
  /** In-block NWI R4 riverine acreage deducted from `total_ac`. */
  streambed_ac: number;
  /** Always ≤ the screened `piece_count` — merging pieces, never splitting. */
  piece_count: number;
  /** Sum over effective pieces of (screened pieces merged into it − 1),
      floored at 0. 0 means this block has no R4 streambed cuts to rejoin —
      effective equals screened and the client renders no secondary line. */
  crossings: number;
  /** One feature per effective piece, each with `ac` (net) and `crossings`
      properties. */
  pieces: GeoJSON.FeatureCollection;
}

/** Every buildable acre inside the opportunity block, not only the screened
    campus square: block minus effective FEMA 100-yr flood minus NWI wetlands
    minus state-maintained road corridors minus 1,000-ft buffers on non-plugged
    wells, kept as contiguous pieces of at least `min_piece_ac` and simplified
    for storage. Areas joined only by a neck under `2 × neck_split_m` wide count
    as separate pieces. Distances and areas are screens, not surveys. */
export interface PortfolioLrmBuildable {
  version: number;
  /** Sum of the kept pieces, acres. */
  total_ac: number;
  piece_count: number | null;
  min_piece_ac: number | null;
  /** Opening radius, metres, that severed necks between pieces: two areas joined
      only by a strip under twice this wide are separate pieces. Absent on
      pre-v4 records. */
  neck_split_m?: number | null;
  method: string | null;
  /** True where effective FEMA coverage is absent. A pending flood_basis is separate. */
  fema_absent: boolean;
  /** True where NWI has no polygons within 5 km of the block (a quad-shaped
      mapping hole) — wetlands unscreened. Absent on pre-v5 records. */
  nwi_absent?: boolean;
  /** One feature per kept piece, each with its own `ac` property. */
  pieces: GeoJSON.FeatureCollection;
  /** v6 and later. Absent (old records never had the key) or `null` (server
      could not validate the payload) on anything else — always null-guard as
      `buildable.effective?`. */
  effective?: BuildableEffective | null;
}

/** NAAQS air-permitting screen: EPA nonattainment/maintenance areas tested
    against the whole opportunity block. `attainment` means no such area
    touches any of the block. */
export interface PortfolioLrmAttainment {
  status: 'attainment' | 'maintenance' | 'nonattainment';
  overlaps: { area: string; pollutant: string | null; status: string | null }[];
  basis: string | null;
}

/** Curated shortlist metadata from Crown Pine's Land Resource Management tract
    inventory. The campus geometry is the selected first-phase footprint;
    block_geometry is the much larger land estate retained for expansion
    analysis. */
export interface PortfolioLrmShortlist {
  block_id: string;
  screen_rank: number;
  tier: 'Advance' | 'Conditional' | 'Queue';
  county: string | null;
  gross_legal_acres: number | null;
  outside_sfha_ac: number | null;
  fema_coverage_pct: number | null;
  flood_basis?: {
    status: 'pending';
    product_id: string;
    effective_date: string;
    source_url: string;
    method_version: string;
  };
  source_tracts: number | null;
  block_geometry: GeoJSON.Geometry;
  campus: {
    acres: number;
    kind: string | null;
    note: string | null;
    avg_slope_pct: number | null;
    pct_under_5_pct: number | null;
    flood_pct: number | null;
    estimated_aquatic_fill_ac: number | null;
    geometry: GeoJSON.Geometry;
  };
  pipeline: {
    name: string | null;
    diameter_in: number | null;
    campus_distance_mi: number | null;
  };
  highway: {
    name: string | null;
    campus_distance_mi: number | null;
    note: string | null;
  };
  gas: PortfolioLrmGas | null;
  buildable?: PortfolioLrmBuildable | null;
  attainment?: PortfolioLrmAttainment | null;
  expansion_status: string | null;
  primary_gate: string | null;
  as_of: string | null;
}

export interface PortfolioPad {
  /** Stable id: `${slug}#${piece_index}`. */
  id: string;
  /** Parent carve-out slug (the ownership block this pad belongs to). */
  slug: string;
  /** 1-based index of this piece within the block's clean-pad MultiPolygon. */
  piece_index: number;
  /** Selected-site letter (A/B/C, or the Site-A carve tracts A1/A2) if this pad is
      part of the land inquiry; null otherwise. */
  site: string | null;
  /** Site-A carve tracts (A1/A2) — surfaced only in the Inquiry-sites view, kept
      out of the full-footprint ranked list. */
  inquiry_only?: boolean;
  /** Curated logistics for the pitch sites (A1, C; A2 inherits A1's). */
  logistics?: PortfolioSiteLogistics;
  /** Curated LRM shortlist screen. Present only on the synthetic first-phase
      campus pad for each shortlisted opportunity block. */
  shortlist?: PortfolioLrmShortlist;
  /** Exact geodesic acreage of this single contiguous pad. */
  acres: number | null;
  /** Area-weighted slope of the parcels overlapping THIS pad. null = no slope data. */
  avg_slope_pct: number | null;
  /** Residual 100-yr flood % inside the pad (≈0 by construction). */
  flood_pct: number | null;
  /** The pad polygon — the map's per-pad feature. */
  geometry: GeoJSON.Geometry | null;
  parent: {
    parcel_id: string;
    site_evaluation_id: string;
    county: string;
    state: string;
    name: string;
    grade: string | null;
    knockout_count: number;
  } | null;
}

export interface PortfolioResponse {
  /** Presentation profile resolved from the requested slug. `crownpine` →
      'research', `pine-curtain-integra` → 'integra' (same underlying cycle),
      every other client → 'default'. */
  profile?: 'research' | 'integra' | 'default';
  cycle: PortfolioCycle;
  totals: PortfolioTotals;
  synthesis: PortfolioSynthesis;
  rows: PortfolioRow[];
  /** Crown Pine research mode only — the per-pad breakdown (see PortfolioPad).
      Absent for every other client (Integra's response is unchanged). */
  pads?: PortfolioPad[];
  /** Crown Pine research mode only — curated grid overlay + interconnection
      queue summary. Absent for every other client. */
  grid_context?: GridContext;
  /** Integra portfolio only — the A1 flagship card's opportunity-block figures
      (the Crown Pine shortlist record the Crown Pine page's card also reads). */
  flagship?: PortfolioFlagship;
}

export interface PortfolioFlagship {
  buildable_ac: number;
  gross_legal_acres: number;
}

export async function fetchPortfolio(clientSlug: string, token?: string): Promise<PortfolioResponse> {
  const url = `/api/portfolio/${encodeURIComponent(clientSlug)}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  const headers: Record<string, string> = {};
  // Bearer auth — same pattern as src/lib/api.ts. Owner pastes APP_PASSWORD
  // into localStorage 'pp-auth' on first load; every subsequent API call
  // attaches it. Cookie auth is not used in this app.
  const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
  if (password) headers['Authorization'] = `Bearer ${password}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Failed to load portfolio: ${res.status}`);
  return res.json();
}

/** One shortlist block's membership in Crown Pine's own LRM tract inventory
    (served by /api/crownpine-parcels/site-tracts; the seller's identifiers). */
export interface SiteTractEntry {
  name: string;
  fund: string;
  acres: number | null;
  partial: boolean;
}
export interface SiteTractsBlock {
  count: number;
  partial_count: number;
  funds: Record<string, number>;
  tracts: SiteTractEntry[];
}

export async function fetchSiteTracts(token?: string): Promise<{ blocks: Record<string, SiteTractsBlock> }> {
  const url = `/api/crownpine-parcels/site-tracts${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  const headers: Record<string, string> = {};
  const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
  if (password) headers['Authorization'] = `Bearer ${password}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Failed to load site tracts: ${res.status}`);
  return res.json();
}
